import React, { useEffect, useRef, useState } from 'react';
import { VerificationConfig } from '@/types';
import {
  Activity,
  Bold,
  Calendar,
  CheckCircle,
  Clock,
  Code,
  FileText,
  Italic,
  List,
  Loader2,
  MapPin,
  Type,
  Underline,
  Upload,
  User
} from 'lucide-react';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { api } from '@/services/api';
import { normalizeErrorMessage } from '@/lib/errorUtils';

// --- ImageUploader ---

interface ImageUploaderProps {
  currentImage?: string;
  onImageChange: (url: string) => void;
  label: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  currentImage,
  onImageChange,
  label
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    setIsUploading(true);
    try {
      const publicUrl = await api.uploadFile(e.target.files[0], 'Venue_Images');
      onImageChange(publicUrl);
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to upload image.'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-bold text-slate-500 uppercase">{label}</label>
      <div className="flex items-start gap-4">
        <div className="w-24 h-24 border rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden relative group">
          {currentImage ? (
            <img src={currentImage} alt="Preview" className="w-full h-full object-contain" />
          ) : (
            <span className="text-xs text-slate-400">No Logo</span>
          )}
        </div>
        <div className="flex-1">
          <label
            className={`cursor-pointer px-4 py-2 rounded border font-medium inline-flex items-center gap-2 text-sm transition-all ${isUploading ? 'bg-slate-50 text-slate-400 border-slate-200' : 'bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-400 shadow-sm'}`}
          >
            {isUploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {isUploading ? 'Uploading...' : 'Select New Logo'}
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={isUploading}
            />
          </label>
          <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
            PNG with transparency (Max height 60px).
          </p>
        </div>
      </div>
    </div>
  );
};

// --- SimpleEditor ---

interface SimpleEditorProps {
  value: string;
  onChange: (val: string) => void;
  disabled: boolean;
  height?: string;
}

export const SimpleEditor: React.FC<SimpleEditorProps> = ({
  value,
  onChange,
  disabled,
  height = '700px'
}) => {
  const [mode, setMode] = useState<'visual' | 'code'>('visual');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current && mode === 'visual' && value !== contentRef.current.innerHTML) {
      contentRef.current.innerHTML = value || '';
    }
  }, [mode, value]);

  const handleExec = (command: string, val?: string) => {
    document.execCommand(command, false, val);
    if (contentRef.current) onChange(contentRef.current.innerHTML);
  };

  return (
    <div
      className="border rounded-xl overflow-hidden bg-white flex flex-col shadow-lg border-slate-200"
      style={{ height }}
    >
      <div className="bg-slate-50 border-b p-2 flex gap-2 items-center flex-wrap sticky top-0 z-20 shadow-sm">
        <div className="flex bg-white rounded-lg border border-slate-300 overflow-hidden shadow-sm mr-2">
          <button
            onClick={() => setMode('visual')}
            className={`px-4 py-2 text-xs font-bold flex items-center gap-1.5 transition-colors ${mode === 'visual' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            <Type className="w-3.5 h-3.5" /> Visual
          </button>
          <button
            onClick={() => setMode('code')}
            className={`px-4 py-2 text-xs font-bold flex items-center gap-1.5 transition-colors ${mode === 'code' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            <Code className="w-3.5 h-3.5" /> HTML
          </button>
        </div>
        {mode === 'visual' && (
          <div className="flex gap-1 bg-white p-1 rounded-lg border shadow-sm">
            <button
              onClick={() => handleExec('bold')}
              className="p-2 hover:bg-slate-100 rounded text-slate-700"
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleExec('italic')}
              className="p-2 hover:bg-slate-100 rounded text-slate-700"
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleExec('underline')}
              className="p-2 hover:bg-slate-100 rounded text-slate-700"
            >
              <Underline className="w-4 h-4" />
            </button>
            <div className="w-px bg-slate-200 mx-1" />
            <button
              onClick={() => handleExec('insertUnorderedList')}
              className="p-2 hover:bg-slate-100 rounded text-slate-700"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-auto relative bg-slate-100">
        {mode === 'visual' ? (
          <div className="p-10 min-h-full flex justify-center">
            <div
              ref={contentRef}
              className="w-full max-w-4xl bg-white shadow-md p-8 outline-none prose prose-sm border border-slate-200"
              contentEditable={!disabled}
              onInput={(e) => onChange(e.currentTarget.innerHTML)}
            />
          </div>
        ) : (
          <textarea
            className="w-full h-full p-6 font-mono text-sm bg-slate-900 text-teal-400 resize-none outline-none leading-relaxed"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
};

// --- VariableList ---

export const VariableList: React.FC = () => (
  <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
    <div className="text-xs font-bold text-teal-400 uppercase mb-3 flex items-center gap-2">
      <Code className="w-4 h-4" /> Available Variables
    </div>
    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
      {[
        { v: '{{patient_name}}', d: 'Full name' },
        { v: '{{booking_ref}}', d: 'Unique ID' },
        { v: '{{appointment_date}}', d: 'Date string' },
        { v: '{{appointment_time}}', d: 'Time string' },
        { v: '{{venue_name}}', d: 'Site name' },
        { v: '{{service_name}}', d: 'Med type' },
        { v: '{{portal_url}}', d: 'Login link' },
        { v: '{{reset_link}}', d: 'Password reset link' },
        { v: '{{company_name}}', d: 'Client name' },
        { v: '{{cancelled_by}}', d: 'Cancelling user' }
      ].map((item) => (
        <div key={item.v} className="flex justify-between items-center group">
          <code
            className="text-[10px] text-teal-300 font-bold bg-white/5 px-1.5 py-0.5 rounded cursor-copy"
            onClick={() => {
              navigator.clipboard.writeText(item.v);
              showAlertModal('Copied variable!');
            }}
          >
            {item.v}
          </code>
          <span className="text-[9px] text-slate-500">{item.d}</span>
        </div>
      ))}
    </div>
  </div>
);

// --- MobileVerificationPreview ---

interface MobileVerificationPreviewProps {
  config: VerificationConfig;
  logoUrl: string;
}

export const MobileVerificationPreview: React.FC<MobileVerificationPreviewProps> = ({
  config,
  logoUrl
}) => (
  <div className="relative mx-auto border-[8px] border-slate-800 rounded-[2.5rem] h-[600px] w-[300px] bg-white shadow-2xl overflow-hidden hidden lg:block sticky top-8">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-800 rounded-b-xl z-20" />
    <div className="h-full w-full bg-slate-50 overflow-y-auto scrollbar-hide">
      <div className="bg-white border-b p-4 flex flex-col items-center pt-10">
        <img
          src={logoUrl || 'https://placehold.co/100x40?text=K2+OH'}
          alt="Logo"
          className="h-8 mb-4 object-contain"
        />
        <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-[10px] font-black flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> VERIFIED
        </div>
      </div>
      <div className="p-4 text-center border-b bg-white">
        <p className="text-[11px] font-medium text-slate-700">{config.validMessage}</p>
      </div>
      <div className="p-4 space-y-4">
        {config.showName && (
          <div className="flex gap-3">
            <div className="bg-blue-50 p-2 rounded-lg text-blue-600 h-fit">
              <User className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                Patient
              </p>
              <p className="font-bold text-slate-900 text-sm">Jane Smith</p>
            </div>
          </div>
        )}
        {config.showRef && (
          <div className="flex gap-3">
            <div className="bg-purple-50 p-2 rounded-lg text-purple-600 h-fit">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                Certificate ID
              </p>
              <p className="font-mono text-slate-700 text-[10px] break-all">K2-CERT-992384</p>
            </div>
          </div>
        )}
        {config.showDate && (
          <div className="flex gap-3">
            <div className="bg-orange-50 p-2 rounded-lg text-orange-600 h-fit">
              <Calendar className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Date</p>
              <p className="font-medium text-slate-800 text-sm">24/05/2024</p>
            </div>
          </div>
        )}
        {config.showExpiry && (
          <div className="flex gap-3">
            <div className="bg-green-50 p-2 rounded-lg text-green-600 h-fit">
              <Clock className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                Expiry
              </p>
              <p className="font-bold text-slate-800 text-sm">24/05/2027</p>
            </div>
          </div>
        )}
        {config.showOutcome && (
          <div className="flex gap-3">
            <div className="bg-teal-50 p-2 rounded-lg text-teal-600 h-fit">
              <Activity className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                Outcome
              </p>
              <p className="font-black text-teal-700 uppercase text-xs">Fit for Duties</p>
            </div>
          </div>
        )}
      </div>
      <div className="p-4 text-center">
        <p className="text-[9px] text-slate-300">Verified Securely by K2 System</p>
      </div>
    </div>
  </div>
);

// --- Shared pure helpers ---

export const formatTaxRateMappingsText = (mappings: Record<string, string> = {}) =>
  Object.entries(mappings)
    .filter(([key, value]) => key && value)
    .map(([key, value]) => `${key}=${value}`)
    .join('\n');

export const parseTaxRateMappingsText = (value: string) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, line) => {
      const [left, ...rest] = line.split('=');
      const key = String(left || '').trim();
      const mappedValue = rest.join('=').trim();
      if (!key || !mappedValue) return acc;
      acc[key] = mappedValue;
      return acc;
    }, {});

export const toUtcBoundary = (value: string, boundary: 'start' | 'end') => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.includes('T')) return trimmed;
  const [year, month, day] = trimmed.split('-').map(Number);
  if (!year || !month || !day) return trimmed;
  const date =
    boundary === 'start'
      ? new Date(year, month - 1, day, 0, 0, 0, 0)
      : new Date(year, month - 1, day, 23, 59, 59, 999);
  return date.toISOString();
};

export const getAuditMetadataSummary = (metadata: Record<string, unknown> | null | undefined) => {
  const safeMetadata = metadata || {};
  const keys = Object.keys(safeMetadata);
  const priorityKeys = ['source', 'reason', 'type'] as const;
  for (const key of priorityKeys) {
    const value = safeMetadata[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return keys.length > 0 ? `${keys.length} fields` : '—';
};
